import {
  Box,
  Checkbox,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useRef, useState } from 'react';

import { useCalendarViewStore } from '../../store/CalendarViewContext';

import s from './CalendarSidebar.module.scss';

const ColorIndicator: React.FC<{ color: string | null }> = ({ color }) => (
  <Box
    sx={{
      width: 16,
      height: 16,
      borderRadius: '50%',
      backgroundColor: color || '#888',
      mr: 1,
      flexShrink: 0,
    }}
  />
);

/** ListItemText with tooltip that only appears when text is truncated */
const TruncatedCalendarName: React.FC<{ name: string }> = ({ name }) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const handleMouseEnter = useCallback(() => {
    const el = textRef.current;

    if (el && el.scrollWidth > el.clientWidth) {
      setShowTooltip(true);
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    setShowTooltip(false);
  }, []);

  return (
    <Tooltip title={name} open={showTooltip} placement="right" arrow>
      <ListItemText
        primary={name}
        primaryTypographyProps={{ variant: 'body2', noWrap: true, ref: textRef }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      />
    </Tooltip>
  );
};

type CalendarSidebarProps = {
  collapsed?: boolean;
  /** Показывать пункт «Черновики» (только при просмотре своего пользователя) */
  showDraftsOption?: boolean;
};

export const CalendarSidebar: React.FC<CalendarSidebarProps> = observer(
  ({ collapsed, showDraftsOption = true }) => {
    const store = useCalendarViewStore();

    return (
      <div className={clsx(s.sidebar, { [s.sidebar_collapsed]: collapsed })}>
        <div className={s.sidebar__header}>
          <Typography variant="subtitle2" className={s.sidebar__title}>
            Календари
          </Typography>
        </div>

        <List dense disablePadding className={s.sidebar__list}>
          {store.calendars.map((calendar) => {
            const isSyncDisabled = !calendar.isSelected;

            const item = (
              <ListItem disablePadding sx={{ opacity: isSyncDisabled ? 0.5 : 1 }}>
                <ListItemButton
                  onClick={() => store.toggleCalendarVisibility(calendar.id)}
                  dense
                  disabled={isSyncDisabled}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox
                      edge="start"
                      checked={!isSyncDisabled && store.isCalendarVisible(calendar.id)}
                      disableRipple
                      disabled={isSyncDisabled}
                      size="small"
                      sx={{
                        color: calendar.color || '#888',
                        '&.Mui-checked': {
                          color: calendar.color || '#888',
                        },
                      }}
                    />
                  </ListItemIcon>
                  <ColorIndicator color={calendar.color} />
                  <TruncatedCalendarName name={calendar.name} />
                </ListItemButton>
              </ListItem>
            );

            if (isSyncDisabled) {
              return (
                <Tooltip
                  key={calendar.id}
                  title="Для включения этого календаря перейдите в настройки синхронизации CalDAV"
                  placement="right"
                  arrow
                >
                  <span>{item}</span>
                </Tooltip>
              );
            }

            return <React.Fragment key={calendar.id}>{item}</React.Fragment>;
          })}
        </List>

        <Divider sx={{ my: 1 }} />

        <List dense disablePadding>
          <ListItem disablePadding>
            <ListItemButton onClick={() => store.toggleOtherLogs()} dense>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Checkbox edge="start" checked={store.showOtherLogs} disableRipple size="small" />
              </ListItemIcon>
              <ListItemText primary="Другие логи" primaryTypographyProps={{ variant: 'body2' }} />
            </ListItemButton>
          </ListItem>
          {showDraftsOption && (
            <ListItem disablePadding>
              <ListItemButton onClick={() => store.toggleShowDrafts()} dense>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Checkbox
                    edge="start"
                    checked={store.showDrafts}
                    disableRipple
                    size="small"
                    sx={{
                      '&.Mui-checked': { color: '#f59e0b' },
                    }}
                  />
                </ListItemIcon>
                <ListItemText primary="Черновики" primaryTypographyProps={{ variant: 'body2' }} />
              </ListItemButton>
            </ListItem>
          )}
        </List>
      </div>
    );
  },
);
